import { call, put, select, takeLatest, delay } from "redux-saga/effects"
import {
  guestHistorySearchFailure,
  guestHistorySearchSuccess,
  GuestHistoryNumericFilter,
  GuestHistoryPaging,
  GuestHistorySearchFilters,
  GuestHistorySearchRequestAction,
  GuestHistorySearchRequestPayload,
  GuestHistorySortOrder,
} from "appRedux/actions/guestHistory"
import {
  GUEST_HISTORY_SEARCH_REQUEST,
} from "constants/ActionTypes"
import { API_URL_V1, API_PMS_HEADERS } from "constants/ApiSetting"
import { APIPostRequest } from "util/connection"
import { getItems } from "util/localStorage"
import { userSignOut } from "appRedux/actions/Auth"
import { AppState } from "appRedux/reducers/ReducerState"

interface GuestHistoryApiResponse {
  status: number
  data?: any
  message?: string
}

const selectLastRequest = (state: AppState) => state.guestHistory?.lastRequest

const normalizeSortOrder = (order?: string | null): GuestHistorySortOrder | undefined => {
  if (!order) return undefined
  if (order === "ascend" || order === "ascending" || order === "asc") return "asc"
  if (order === "descend" || order === "descending" || order === "desc") return "desc"
  return undefined
}

const parseNumericFilter = (
  raw: GuestHistoryNumericFilter | number | string | null | undefined
): GuestHistoryNumericFilter | undefined => {
  if (raw === null || raw === undefined) return undefined
  if (typeof raw === "object") {
    const candidate = raw as GuestHistoryNumericFilter
    if (typeof candidate.value === "number" && candidate.op) {
      return candidate
    }
    return undefined
  }
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return { op: ">=", value: raw }
  }
  if (typeof raw === "string") {
    const trimmed = raw.trim()
    if (!trimmed) return undefined
    const numeric = Number(trimmed)
    if (!Number.isNaN(numeric)) {
      return { op: ">=", value: numeric }
    }
    const pattern = /^([<>]=?)\s*(\d+)$/i
    const match = trimmed.match(pattern)
    if (match) {
      const [, op, value] = match
      const parsed = Number(value)
      if (!Number.isNaN(parsed)) {
        return { op: op as GuestHistoryNumericFilter["op"], value: parsed }
      }
    }
  }
  return undefined
}

const sanitizeFilters = (filters?: GuestHistorySearchFilters): GuestHistorySearchFilters => {
  if (!filters) {
    return {}
  }
  const clean: Partial<GuestHistorySearchFilters> = {}
  const keys = Object.keys(filters) as Array<keyof GuestHistorySearchFilters>

  keys.forEach((key) => {
    const value = filters[key]
    if (value === null || value === undefined) return

    switch (key) {
      case "visit_time":
      case "visit_night": {
        const parsed = parseNumericFilter(value as GuestHistoryNumericFilter | number | string)
        if (parsed) {
          clean[key] = parsed
        }
        return
      }
      case "nation_id":
      case "country_id":
      case "vip_id":
      case "channel_id":
      case "roomtype_id":
      case "rateplan_id": {
        if (typeof value === "string") {
          const trimmed = value.trim()
          if (!trimmed) return
          clean[key] = trimmed as GuestHistorySearchFilters[typeof key]
        } else if (typeof value === "number") {
          clean[key] = value
        }
        return
      }
      default: {
        if (typeof value === "string") {
          const trimmed = value.trim()
          if (!trimmed) return
          clean[key] = trimmed as GuestHistorySearchFilters[typeof key]
        }
        return
      }
    }
  })
  return clean as GuestHistorySearchFilters
}

const buildRequestPayload = (
  current: GuestHistorySearchRequestPayload | undefined,
  incoming: GuestHistorySearchRequestPayload
): GuestHistorySearchRequestPayload => {
  const mergedFilters = incoming.replaceFilters
    ? { ...(incoming.filters || {}) }
    : { ...(current?.filters || {}), ...(incoming.filters || {}) }

  const basePaging = incoming.replaceFilters ? {} : current?.paging || {}
  const mergedPaging = {
    ...basePaging,
    ...(incoming.paging || {}),
  }

  // Default page reset when filters change and page not explicitly provided
  if (incoming.replaceFilters && incoming.paging?.page === undefined) {
    mergedPaging.page = 1
  }

  return {
    filters: sanitizeFilters(mergedFilters),
    paging: mergedPaging,
    replaceFilters: incoming.replaceFilters,
  }
}

const ensurePaging = (
  request: GuestHistorySearchRequestPayload
): Required<GuestHistorySearchRequestPayload>["paging"] => {
  const page = Number(request.paging?.page ?? 1)
  const limit = Number(request.paging?.limit ?? 20)
  const sort_field = request.paging?.sort_field || "id"
  const sort_order = normalizeSortOrder(request.paging?.sort_order) || "desc"
  return { page, limit, sort_field, sort_order }
}

const normalizeResponsePaging = (
  responsePaging: any,
  requestPaging: ReturnType<typeof ensurePaging>,
  itemCount: number
): GuestHistoryPaging => {
  const current = Number(
    responsePaging?.current ??
      responsePaging?.page ??
      requestPaging.page ??
      1
  )
  const limit = Number(
    responsePaging?.limit ??
      responsePaging?.pageSize ??
      requestPaging.limit ??
      20
  )
  const totalRows = Number(
    responsePaging?.total_rows ??
      responsePaging?.total ??
      responsePaging?.row_count ??
      itemCount ??
      0
  )
  const rowCount = Number(
    responsePaging?.row_count ??
      itemCount ??
      Math.min(totalRows, limit)
  )
  const sortField =
    responsePaging?.sort_field ?? requestPaging.sort_field
  const sortOrder = normalizeSortOrder(
    responsePaging?.sort_order ?? requestPaging.sort_order
  )

  return {
    current: Number.isNaN(current) ? 1 : current,
    limit: Number.isNaN(limit) ? 20 : limit,
    total_rows: Number.isNaN(totalRows) ? itemCount : totalRows,
    row_count: Number.isNaN(rowCount) ? itemCount : rowCount,
    sort_field: sortField,
    sort_order: sortOrder,
  }
}

function* handleGuestHistorySearch(action: GuestHistorySearchRequestAction) {
  try {
    const lastRequest: GuestHistorySearchRequestPayload | undefined = yield select(selectLastRequest)
    const requestPayload = buildRequestPayload(lastRequest, action.payload || {})
    const paging = ensurePaging(requestPayload)

    const defaultFilters = {
      guest_name: null as string | null,
      doc_number: null as string | null,
      member_code: null as string | null,
      email: null as string | null,
      telephone: null as string | null,
      nation_id: null as number | null,
      country_id: null as number | null,
      vip_id: null as number | null,
      birthdate: null as string | null,
      arrival: null as string | null,
      departure: null as string | null,
      stay_date: null as string | null,
      channel_id: null as number | null,
      company: null as string | null,
      visit_time: { op: ">=", value: 1 } as { op: ">=" | ">" | "<=" | "<" | "="; value: number },
      visit_night: { op: ">=", value: 1 } as { op: ">=" | ">" | "<=" | "<" | "="; value: number },
    }

    const mergedFilters = {
      ...defaultFilters,
      ...(requestPayload.filters || {}),
      // ensure numeric filter objects are present if UI didn't provide them
      visit_time: (requestPayload.filters && (requestPayload.filters as any).visit_time) ? (requestPayload.filters as any).visit_time : defaultFilters.visit_time,
      visit_night: (requestPayload.filters && (requestPayload.filters as any).visit_night) ? (requestPayload.filters as any).visit_night : defaultFilters.visit_night,
    }

    const requestBody: Record<string, unknown> = {
      ...mergedFilters,
      paging: {
        page: paging.page,
        limit: paging.limit,
        ...(paging.sort_field ? { sort_field: paging.sort_field } : {}),
        ...(paging.sort_order ? { sort_order: paging.sort_order } : {}),
      },
    }

    const { access_token } = yield call(getItems, ["access_token"])
    API_PMS_HEADERS.headers.Authorization = `Bearer ${access_token}`

    const url = `${API_URL_V1}pms/rpc/guest_search`
    const response: GuestHistoryApiResponse = yield call(
      APIPostRequest,
      url,
      requestBody,
      API_PMS_HEADERS
    )

    if (response.status === 200) {
      const rawData = response.data || {}
      const envelope = rawData.data || rawData
      const resultData = envelope.result_data || envelope.data || envelope
      const items = Array.isArray(resultData.items)
        ? resultData.items
        : Array.isArray(resultData.guests)
          ? resultData.guests
          : []

      const pagingInfo = resultData.paging || resultData.meta || {}
      const normalizedPaging = normalizeResponsePaging(
        pagingInfo,
        paging,
        items.length
      )

      yield put(
        guestHistorySearchSuccess({
          items,
          paging: normalizedPaging,
          result_code: envelope.result_code ?? rawData.result_code ?? null,
          result_msg: envelope.result_msg ?? rawData.result_msg ?? null,
          request: {
            filters: requestPayload.filters,
            paging: {
              page: normalizedPaging.current,
              limit: normalizedPaging.limit,
              sort_field: normalizedPaging.sort_field,
              sort_order: normalizedPaging.sort_order,
            },
            replaceFilters: requestPayload.replaceFilters,
          },
        })
      )
      yield delay(100)
      return
    }

    const message =
      response?.data?.message ||
      response?.message ||
      "Unable to load guest history search results"
    const result_code =
      response?.data?.result_code ??
      response?.data?.data?.result_code ??
      null

    if (response.status === 403) {
      yield put(userSignOut())
      return
    }

    yield put(
      guestHistorySearchFailure({
        message,
        status: response.status,
        result_code,
        response,
      })
    )
    yield delay(100)
  } catch (error: any) {
    const message = error?.message || "Unable to load guest history search results"
    yield put(
      guestHistorySearchFailure({
        message,
        response: error,
      })
    )
    yield delay(100)
  }
}

export default function* guestHistorySaga() {
  yield takeLatest(GUEST_HISTORY_SEARCH_REQUEST, handleGuestHistorySearch)
}
